import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  PhoneAndroid as MobileIcon,
} from '@mui/icons-material';

const ContactNumbers = ({ 
  value = [], 
  onChange, 
  error, 
  helperText,
  label = "Contact Numbers",
  disabled = false 
}) => {
  const [contactNumbers, setContactNumbers] = useState(value);

  useEffect(() => {
    setContactNumbers(value);
  }, [value]);

  const handleAddNumber = () => {
    const newNumber = {
      type: 'phone',
      number: '',
      tempId: Date.now() // Temporary ID for new items
    };
    const updatedNumbers = [...contactNumbers, newNumber];
    setContactNumbers(updatedNumbers);
    onChange(updatedNumbers);
  };

  const handleRemoveNumber = (index) => {
    const updatedNumbers = contactNumbers.filter((_, i) => i !== index);
    setContactNumbers(updatedNumbers);
    onChange(updatedNumbers);
  };

  const handleNumberChange = (index, field, newValue) => {
    const updatedNumbers = contactNumbers.map((item, i) => 
      i === index ? { ...item, [field]: newValue } : item
    );
    setContactNumbers(updatedNumbers);
    onChange(updatedNumbers);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'phone':
        return <PhoneIcon fontSize="small" />;
      case 'mobile':
        return <MobileIcon fontSize="small" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'phone':
        return 'primary';
      case 'mobile':
        return 'secondary';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="label">
          {label}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddNumber}
          disabled={disabled}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Add Phone/Mobile
        </Button>
      </Box>

      {contactNumbers.length === 0 ? (
        <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">
            No contact numbers added yet. Click "Add Phone/Mobile" to add one.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {contactNumbers.map((contactNumber, index) => (
            <Grid item xs={12} sm={6} md={4} key={contactNumber.id || contactNumber.tempId || index}>
              <Card 
                variant="outlined" 
                sx={{ 
                  position: 'relative',
                  '&:hover': { 
                    boxShadow: 2,
                    '& .delete-button': { 
                      opacity: 1 
                    }
                  }
                }}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  {/* Delete Button */}
                  <IconButton
                    className="delete-button"
                    onClick={() => handleRemoveNumber(index)}
                    disabled={disabled}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      opacity: 0.7,
                      transition: 'opacity 0.2s',
                      bgcolor: 'error.main',
                      color: 'white',
                      width: 28,
                      height: 28,
                      '&:hover': {
                        bgcolor: 'error.dark',
                        opacity: 1
                      }
                    }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  {/* Type Chip */}
                  <Box mb={2}>
                    <Chip
                      icon={getTypeIcon(contactNumber.type)}
                      label={contactNumber.type.charAt(0).toUpperCase() + contactNumber.type.slice(1)}
                      color={getTypeColor(contactNumber.type)}
                      size="small"
                    />
                  </Box>

                  {/* Type Select */}
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={contactNumber.type || ''}
                      onChange={(e) => handleNumberChange(index, 'type', e.target.value)}
                      label="Type"
                      disabled={disabled}
                    >
                      <MenuItem value="phone">
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon fontSize="small" />
                          Phone
                        </Box>
                      </MenuItem>
                      <MenuItem value="mobile">
                        <Box display="flex" alignItems="center" gap={1}>
                          <MobileIcon fontSize="small" />
                          Mobile
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Number Input */}
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Number"
                    placeholder="Enter phone number"
                    value={contactNumber.number || ''}
                    onChange={(e) => handleNumberChange(index, 'number', e.target.value)}
                    disabled={disabled}
                    size="small"
                    error={error && (!contactNumber.number || !contactNumber.type)}
                    helperText={
                      error && (!contactNumber.number || !contactNumber.type) 
                        ? 'Both type and number are required' 
                        : ''
                    }
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Global Error Message */}
      {error && helperText && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}

      {/* Summary */}
      {contactNumbers.length > 0 && (
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="body2" color="text.secondary">
            Total contact numbers: {contactNumbers.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ContactNumbers;